import React, { useState, useEffect, useCallback, useContext } from "react";
import {
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    SelectChangeEvent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    DataGrid,
    GridColDef,
    GridRowId,
    GridRenderCellParams,
    GridValidRowModel, // Import GridValidRowModel
} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import { ThemeContext } from "../../App";

// Define a type for rows that includes the _gridId used by DataGrid
type ProcessedTableRow = GridValidRowModel & { _gridId: GridRowId } & Record<
        string,
        unknown
    >;

const DatabaseManager: React.FC = () => {
    const [tables, setTables] = useState<string[]>([]);
    const [selectedTable, setSelectedTable] = useState<string>("");
    const [tableData, setTableData] = useState<ProcessedTableRow[]>([]); // Use ProcessedTableRow
    const [columns, setColumns] = useState<GridColDef<ProcessedTableRow>[]>([]); // Specify row type for GridColDef
    const [loadingTables, setLoadingTables] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [rowToDelete, setRowToDelete] = useState<GridRowId | null>(null); // State for the ID to delete
    const { isDarkMode } = useContext(ThemeContext);
    const theme = useTheme();

    const fetchTables = useCallback(async () => {
        setLoadingTables(true);
        setError(null);
        try {
            const response = await fetch("/database/tables", {
                credentials: "include",
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(
                    errData.message ||
                        `Failed to fetch tables: ${response.statusText}`
                );
            }
            const data: string[] = await response.json();
            setTables(data);
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError("An unknown error occurred while fetching tables.");
            }
        } finally {
            setLoadingTables(false);
        }
    }, []);

    const fetchDataForTable = useCallback(async (tableName: string) => {
        if (!tableName) return;
        setLoadingData(true);
        setError(null);
        setTableData([]);
        setColumns([]);

        try {
            const response = await fetch(`/database/tables/${tableName}`, {
                credentials: "include",
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.message ||
                        `Failed to fetch data for table ${tableName}`
                );
            }
            const data: Record<string, unknown>[] = await response.json(); // API returns generic objects

            if (data.length > 0) {
                const firstRow = data[0];
                const generatedColumns: GridColDef<ProcessedTableRow>[] =
                    Object.keys(firstRow).map((key) => ({
                        field: key,
                        headerName:
                            key.charAt(0).toUpperCase() +
                            key
                                .slice(1)
                                .replace(/([A-Z])/g, " $1")
                                .trim(),
                        minWidth: 150,
                        flex: 1,
                        editable: false,
                    }));

                // Check if the original data items have an 'id' property that can be used for deletion
                const hasDatabaseIdColumn =
                    Object.keys(firstRow).includes("id");

                if (hasDatabaseIdColumn) {
                    generatedColumns.push({
                        field: "actions",
                        headerName: "Actions",
                        sortable: false,
                        filterable: false,
                        width: 100,
                        renderCell: (
                            params: GridRenderCellParams<ProcessedTableRow>
                        ) => {
                            // params.id is the GridRowId for the current row (derived from _gridId).
                            // params.row.id is the original 'id' field from the database row.
                            const originalId = params.row.id;
                            const canDelete =
                                typeof originalId === "string" ||
                                typeof originalId === "number";

                            return (
                                <IconButton
                                    onClick={() => {
                                        // Pass the grid's row ID (params.id) to the delete dialog.
                                        // This ID was derived from the database 'id' if it was suitable.
                                        handleOpenDeleteDialog(params.id);
                                    }}
                                    color="error"
                                    disabled={!canDelete} // Disable if original 'id' wasn't string/number
                                >
                                    <DeleteIcon />
                                </IconButton>
                            );
                        },
                    });
                }
                setColumns(generatedColumns);

                setTableData(
                    data.map((row, index) => {
                        const idCandidate = row.id; // This is the 'id' field from the database row (unknown)
                        let finalGridId: GridRowId;

                        if (
                            typeof idCandidate === "string" ||
                            typeof idCandidate === "number"
                        ) {
                            finalGridId = idCandidate;
                        } else {
                            // If original 'id' is not string/number, or undefined, use internal id.
                            finalGridId = `_internal_${index}`;
                        }
                        return {
                            ...row,
                            _gridId: finalGridId,
                        } as ProcessedTableRow;
                    })
                );
            } else {
                setTableData([]);
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(
                    "An unknown error occurred while fetching table data."
                );
            }
        } finally {
            setLoadingData(false);
        }
    }, []);

    useEffect(() => {
        fetchTables();
    }, [fetchTables]);

    const handleTableChange = (event: SelectChangeEvent<string>) => {
        const newTable = event.target.value as string;
        setSelectedTable(newTable);
        if (newTable) {
            fetchDataForTable(newTable);
        } else {
            setTableData([]);
            setColumns([]);
        }
    };

    const handleOpenDeleteDialog = (id: GridRowId) => {
        setRowToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setRowToDelete(null);
    };

    const handleDeleteRow = async () => {
        if (rowToDelete === null || !selectedTable) return;
        setError(null);

        const idForBackend = Number(String(rowToDelete));
        if (isNaN(idForBackend) || !Number.isInteger(idForBackend)) {
            setError(
                `Invalid ID for deletion: "${String(
                    rowToDelete
                )}". ID must be an integer.`
            );
            return;
        }

        try {
            const response = await fetch(
                `/database/tables/${selectedTable}/row/${idForBackend}`,
                {
                    method: "DELETE",
                    credentials: "include",
                }
            );
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    throw new Error(
                        `Failed to delete row ${idForBackend}. Server responded with ${response.status}: ${response.statusText}`
                    );
                }
                throw new Error(
                    errorData?.message ||
                        `Failed to delete row ${idForBackend}. Status: ${response.status}`
                );
            }
            await fetchDataForTable(selectedTable);
            handleCloseDeleteDialog();
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(
                    "An unknown error occurred while trying to delete the row."
                );
            }
        }
    };

    const getRowId = (row: ProcessedTableRow): GridRowId => row._gridId; // Now type-safe

    return (
        <Paper
            sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{ color: isDarkMode ? "#ffffff" : "#212529" }}>
                Database Manager
            </Typography>

            {loadingTables && <CircularProgress sx={{ alignSelf: "center" }} />}
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {!loadingTables && tables.length > 0 && (
                <FormControl fullWidth variant="outlined">
                    <InputLabel
                        id="select-table-label"
                        sx={{
                            color: isDarkMode
                                ? "rgba(255,255,255,0.7)"
                                : "inherit",
                        }}>
                        Select Table
                    </InputLabel>
                    <Select
                        labelId="select-table-label"
                        value={selectedTable}
                        label="Select Table"
                        onChange={handleTableChange}
                        sx={{
                            color: isDarkMode ? "#ffffff" : "inherit",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: isDarkMode
                                    ? "rgba(255,255,255,0.23)"
                                    : "rgba(0,0,0,0.23)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: isDarkMode
                                    ? "rgba(255,255,255,0.5)"
                                    : "rgba(0,0,0,0.5)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: isDarkMode
                                    ? theme.palette.primary.main
                                    : theme.palette.primary.main,
                            },
                            "& .MuiSvgIcon-root": {
                                color: isDarkMode
                                    ? "rgba(255,255,255,0.7)"
                                    : "inherit",
                            },
                        }}>
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {tables.map((table) => (
                            <MenuItem key={table} value={table}>
                                {table}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {!loadingTables && tables.length === 0 && !error && (
                <Typography
                    sx={{
                        color: isDarkMode ? "rgba(255,255,255,0.7)" : "inherit",
                    }}>
                    No tables found or unable to load tables.
                </Typography>
            )}

            {loadingData && (
                <CircularProgress sx={{ alignSelf: "center", mt: 2 }} />
            )}

            {selectedTable && !loadingData && tableData.length > 0 && (
                <Box
                    sx={{
                        height: 600,
                        width: "100%",
                        "& .MuiDataGrid-root": {
                            borderColor: isDarkMode
                                ? "rgba(255,255,255,0.23)"
                                : "rgba(0,0,0,0.23)",
                            color: isDarkMode
                                ? "rgba(255,255,255,0.87)"
                                : "inherit",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "#f5f5f5",
                            color: isDarkMode
                                ? "rgba(255,255,255,0.87)"
                                : "inherit",
                            borderColor: isDarkMode
                                ? "rgba(255,255,255,0.23)"
                                : "rgba(0,0,0,0.23)",
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: isDarkMode
                                ? "rgba(255,255,255,0.23)"
                                : "rgba(0,0,0,0.23)",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            backgroundColor: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "#f5f5f5",
                            color: isDarkMode
                                ? "rgba(255,255,255,0.87)"
                                : "inherit",
                            borderColor: isDarkMode
                                ? "rgba(255,255,255,0.23)"
                                : "rgba(0,0,0,0.23)",
                        },
                        "& .MuiTablePagination-root": {
                            color: isDarkMode
                                ? "rgba(255,255,255,0.87)"
                                : "inherit",
                        },
                        "& .MuiIconButton-root": {
                            color: isDarkMode
                                ? "rgba(255,255,255,0.7)"
                                : "inherit",
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: isDarkMode
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                        },
                    }}>
                    <DataGrid
                        rows={tableData}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        getRowId={getRowId}
                        density="compact"
                        autoHeight={false}
                    />
                </Box>
            )}
            {selectedTable &&
                !loadingData &&
                tableData.length === 0 &&
                !error && (
                    <Typography
                        sx={{
                            mt: 2,
                            color: isDarkMode
                                ? "rgba(255,255,255,0.7)"
                                : "inherit",
                        }}>
                        No data found for table '{selectedTable}' or table is
                        empty.
                    </Typography>
                )}

            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete row with ID:{" "}
                        {String(rowToDelete)} from table '{selectedTable}'? This
                        action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDeleteRow} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default DatabaseManager;
